/** @file Handles the username and password form for new login and registration. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as icons from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import * as form from '@/components/Form';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
import { clsButtonLikeLink, clsInput } from './cls';
import { ConfirmEndingPreviousSession } from './ConfirmEndingPreviousSession';
import { routeForgotPassword } from './routes';
/** Form for logging in with a username and password for a specific tenant. */
export const UsernameAndPasswordForm = tsx.component({
    name: 'username-and-password-form',
    props: {
        tenant: p.ofType().required,
        submit: p.ofType().required,
    },
    /** @returns State of the component. */
    data() {
        return {
            username: Maybe.empty(),
            password: Maybe.empty(),
            isolatedSessionError: Maybe.empty(),
            confirmTerminatePrevSession: false,
        };
    },
    methods: {
        /** Dismiss the isolated session error. */
        dismissIsolatedSessionError() {
            this.isolatedSessionError.ifJust(evt => evt.reset({ focusButtonAfterClose: false }));
            this.isolatedSessionError = Maybe.empty();
            this.confirmTerminatePrevSession = false;
        },
    },
    /** @returns The rendered username and password form JSX. */
    render() {
        return (<div class="contents">
                <form.Root data-cy="username-and-password" onBeforeSubmit={() => {
                // Update the component values to trigger validation.
                this.username = this.username.alt(Maybe.of(''));
                this.password = this.password.alt(Maybe.of(''));
            }} handleSubmit={async () => {
                const username = this.username.throwOnEmpty(Error('Username must not be empty'));
                const password = this.password.throwOnEmpty(Error('Password must not be empty'));
                return await this.submit({
                    username,
                    password,
                    terminateIsolatedSession: this.confirmTerminatePrevSession,
                });
            }} submitLoadingMessage="Logging in" submitSuccessMessage="Logged in" submitLabel="Log in" submitOnEnter actionBarVariant="wide-submit" validationOptions={{
                validationErrorMessage: 'Please enter both the username and password to continue',
            }} warningOptions={{
                header: (<em class="block title-small mb-2">
                                Before we proceed to login{utils.ELLIPSIS}
                            </em>),
                dismissButtonLabel: 'Understood and continue to login',
                hideSubmitButtonInWarningState: true,
                focusDismissWarningButton: true,
            }} submitDisabledReason={this.isolatedSessionError.isJust() && !this.confirmTerminatePrevSession
                ? {
                    type: 'tooltip',
                    reason: 'To login, you must confirm that you want to terminate the isolated session.',
                }
                : undefined} onError={errEvt => {
                if (utils.isApiErrorWithCode(errEvt.error, 'ISOLATED_SESSION_ACTIVE')) {
                    this.isolatedSessionError = Maybe.of(errEvt);
                }
            }} scopedSlots={{
                formError: err => {
                    if (utils.isApiErrorWithCode(err, 'ISOLATED_SESSION_ACTIVE')) {
                        return (<ConfirmEndingPreviousSession value={this.confirmTerminatePrevSession} onInput={val => {
                                this.confirmTerminatePrevSession = val;
                            }}/>);
                    }
                    return utils.getErrorMessage(err);
                },
            }}>
                    <form.Field label="Username">
                        <form.Input data-cy="username" class={clsInput} type="text" autocomplete="username" value={this.username.extract()} onInput={val => {
                this.username = Maybe.of(val);
                this.dismissIsolatedSessionError();
            }} required v-focus/>
                        <form.Description class="sr-only">
                            To login, enter your username for {this.tenant.name}
                        </form.Description>
                    </form.Field>

                    <form.Field label="Password">
                        <form.Input data-cy="password" class={clsInput} type="password" autocomplete="current-password" value={this.password.extract()} onInput={val => {
                this.password = Maybe.of(val);
                this.dismissIsolatedSessionError();
            }} required/>
                        <form.Description class="sr-only">Enter your password</form.Description>
                    </form.Field>
                </form.Root>

                <router-link to={routeForgotPassword(this.$router, this.tenant.id)} class={[clsButtonLikeLink, 'mt-2']}>
                    Forgot password?
                    <Icon icon={icons.ArrowRight}/>
                </router-link>
            </div>);
    },
});
